/**
 * EncounterTelevisit — modality-driven telemedicine panel (PROD-1276).
 *
 * Faithful to Flutter `encounter_screen.dart`: when the visit is Virtual and the clinician has
 * pressed the banner's Connect, the encounter shows the floating video widget, which embeds the
 * telemedicine app (`${videoBaseUrl}/?AppointmentId=&ProviderId=`). That app carries the live
 * video, its own call controls, in-call chat, participants and sharing, and asks for camera and
 * microphone itself.
 *
 * ── The Flutter widget, verbatim (Andrew, 2026-11-01) ──────────────────────────────────────────
 * "The UI of the video is different and it should not be changed from the Flutter one." The DS
 * `TelevisitPanel` `variant="bare"` (Signal DS 3.2.0) draws exactly the Flutter
 * FloatingVideoWidget: the iframe in a white box with a corner expand / shrink glyph, 200×111 at
 * the top-left, FULL over the whole encounter area. So this host no longer has:
 *   · the pre-join "Enable camera & microphone" modal — Flutter has none; Connect opens the call;
 *   · the header, Live chip, timer and mic / camera / End consult buttons, which could not reach
 *     the cross-origin iframe anyway; leaving is the banner's Leave, as in Flutter;
 *   · `fullTop` (PA #822): Flutter's FULL covers the banner too, and Shrink brings it back.
 *
 * ── Where the panel lives ──────────────────────────────────────────────────────────────────────
 * Flutter positions the widget in the encounter screen's Stack: right of the nav rail, below the
 * top bar, not scrolled with the page. The PA equivalent is the app shell's overlay layer over the
 * workspace frame (`TELEVISIT_LAYER_ID`, app-shell.tsx), so the panel is portalled there. Without
 * that layer (tests, stories) it renders in place.
 *
 * Renders nothing for non-virtual visits or when telemedicine isn't configured for the environment
 * (empty base). For a Virtual visit that's configured but can't form a session URL (missing
 * appointment/provider id), it shows the visible DS `TelevisitUnavailable` note rather than
 * silently rendering nothing.
 *
 * The native DS comms dock (TelevisitDock — chat/participants/documents) stays deferred: the
 * telemedicine app inside the iframe already has those.
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  TelevisitPanel,
  TelevisitUnavailable,
  type TelevisitPanelSize,
} from '@scripsteam/scrips-signal-ds';
import { env } from '@/config/env';
import { TELEVISIT_LAYER_ID } from '@/app/televisit-layer';
import { buildVideoCallUrl, isVirtualLocation } from './lib/to-televisit';

export interface EncounterTelevisitProps {
  /** Appointment location name — the panel renders only when this is "Virtual". */
  locationName?: string | null;
  appointmentId?: string | null;
  /** Clinician on the call (nurse → practitionerId, else user id). */
  providerId?: string | null;
  patientName: string;
  /** Override the telemedicine base (defaults to the env value). */
  videoBaseUrl?: string;
}

export function EncounterTelevisit({
  locationName,
  appointmentId,
  providerId,
  patientName,
  videoBaseUrl = env.videoBaseUrl,
}: EncounterTelevisitProps) {
  const [size, setSize] = useState<TelevisitPanelSize>('small');
  // The app shell's layer, looked up ONCE, before the first render commits. It never changes after,
  // so the panel never moves between the portal and its own place: a move would remount the iframe
  // and reload (re-join) the call. Not an effect: an effect would find the layer only after the
  // first commit and move the panel once. The shell renders the layer before any route content, so
  // it exists by the time Connect mounts this; without it (tests, stories) the panel stays in place.
  // The layer cannot go stale during a call: it lives in AppShellFrame, the layout route this page
  // renders under, so a shell remount unmounts and remounts this component (and its lookup) too.
  const [layer] = useState<HTMLElement | null>(() =>
    typeof document === 'undefined' ? null : document.getElementById(TELEVISIT_LAYER_ID),
  );

  // Non-virtual visits get nothing.
  if (!isVirtualLocation(locationName)) return null;
  // Telemedicine not configured for this environment → feature off (no noise).
  if (!videoBaseUrl) return null;

  const videoSrc = buildVideoCallUrl(videoBaseUrl, appointmentId, providerId);
  // Configured, but this virtual visit can't form a session URL (missing
  // appointment/provider id) — surface a visible state rather than silently
  // showing no video for a telehealth appointment.
  if (!videoSrc) {
    return <TelevisitUnavailable />;
  }

  const panel = (
    <TelevisitPanel
      variant="bare"
      size={size}
      patient={{ name: patientName }}
      videoSrc={videoSrc}
      onToggleSize={setSize}
      // The layer ignores the pointer so the encounter under it stays usable; the panel takes it back.
      className="pointer-events-auto"
    />
  );

  return layer ? createPortal(panel, layer) : panel;
}
